import { View, Dialog } from 'web-mojo';

/**
 * StopRequestReviewDrawer — planner reviews an operator-submitted field stop request.
 *
 * If the linked route is in_progress, shows an insertion form pre-filled with request data.
 * On "Approve & Insert": inserts the stop into the route then marks the request approved.
 * On "Approve" (non-in_progress route): marks approved only (stop must be manually added later).
 * On "Reject": confirms then marks rejected.
 *
 * Emits 'approved' or 'rejected' on completion.
 */
export default class StopRequestReviewDrawer extends View {
    constructor({ request }) {
        super();
        this.request = request;   // minimal list object
        this.fullRequest = null;  // loaded on init
        this.route = null;
        this.stops = [];
    }

    async onInit() {
        const res = await app.api.get(`/api/routes/stop_request/${this.request.id}`);
        if (res.success && res.data.status) {
            this.fullRequest = res.data.data;
        } else {
            this.fullRequest = { ...this.request };
        }

        if (this.fullRequest.route?.id) {
            const routeId = this.fullRequest.route.id;
            const [routeRes, stopsRes] = await Promise.all([
                app.api.get(`/api/routes/route/${routeId}`, { graph: 'detail' }),
                app.api.get('/api/routes/stop', { route: routeId, sort: 'sequence_position', size: 200 }),
            ]);
            if (routeRes.success && routeRes.data.status) {
                this.route = routeRes.data.data;
                this.stops = (stopsRes.success && stopsRes.data.status)
                    ? stopsRes.data.data || []
                    : [];
            }
        }

        this.render();
    }

    _operatorName() {
        const op = this.fullRequest?.operator || this.request.operator;
        return op?.display_name || op?.user?.username || `Operator ${op?.id || ''}`;
    }

    _formatTime(iso) {
        if (!iso) return '';
        return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }

    _formatDatetime(iso) {
        if (!iso) return '—';
        return new Date(iso).toLocaleString(undefined, {
            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
        });
    }

    async onActionApproveInsert() {
        const activityType = this.element.querySelector('[name=activity_type]')?.value;
        if (!activityType) {
            app.toast.warning('Activity type is required');
            return;
        }

        const label = this.element.querySelector('[name=label]')?.value?.trim();
        const afterStopId = this.element.querySelector('[name=after_stop_id]')?.value;
        const dwellMins = parseInt(this.element.querySelector('[name=dwell_minutes]')?.value || '0') || null;
        const priority = this.element.querySelector('[name=priority]')?.value || 'normal';
        const plannerNote = this.element.querySelector('[name=planner_note]')?.value?.trim() || null;

        const req = this.fullRequest;
        const insertPayload = {
            action: 'insert_stop',
            activity_type: activityType,
            priority,
        };
        if (label) insertPayload.label = label;
        if (req.latitude && req.longitude) {
            insertPayload.lat = req.latitude;
            insertPayload.lng = req.longitude;
        }
        if (req.address) insertPayload.address = req.address;
        if (afterStopId) insertPayload.after_stop_id = parseInt(afterStopId);
        if (dwellMins) insertPayload.dwell_minutes = dwellMins;
        if (plannerNote) insertPayload.planner_note = plannerNote;

        const insertRes = await app.api.patch(`/api/routes/route/${this.route.id}`, insertPayload);
        if (!insertRes.success || !insertRes.data.status) {
            app.toast.error(insertRes.data?.error || 'Failed to insert stop');
            return;
        }

        // Mark request approved
        await app.api.patch(`/api/routes/stop_request/${req.id}`, { status: 'approved' });

        app.toast.success('Stop inserted and request approved');
        this.emit('approved');
    }

    async onActionApproveOnly() {
        const res = await app.api.patch(`/api/routes/stop_request/${this.fullRequest.id}`, {
            status: 'approved',
        });
        if (!res.success || !res.data.status) {
            app.toast.error(res.data?.error || 'Failed to approve request');
            return;
        }
        app.toast.success('Request approved');
        this.emit('approved');
    }

    async onActionReject() {
        const confirmed = await Dialog.confirm({
            title: 'Reject Request',
            message: `Reject the field stop request from ${this._operatorName()}? The operator will be notified.`,
            confirmText: 'Reject',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;

        const res = await app.api.patch(`/api/routes/stop_request/${this.fullRequest.id}`, {
            status: 'rejected',
        });
        if (!res.success || !res.data.status) {
            app.toast.error(res.data?.error || 'Failed to reject request');
            return;
        }
        app.toast.success('Request rejected');
        this.emit('rejected');
    }

    set template(v) { this._t = v; }

    get template() {
        if (!this.fullRequest) {
            return `<div class="p-4 text-center text-muted">
                <div class="spinner-border spinner-border-sm"></div>
            </div>`;
        }

        const req = this.fullRequest;
        const operatorName = this._operatorName();
        const isLive = this.route?.status === 'in_progress';
        const routeLabel = this.route
            ? `${this.route.template?.name || `Route ${this.route.id}`} (${this.route.status})`
            : 'No route linked';

        const afterStopOptions = this.stops
            .filter(s => !['removed', 'cancelled'].includes(s.status))
            .map((s, i) => {
                const label = s.label || s.location?.name || `Stop ${i + 1}`;
                return `<option value="${s.id}">${label}</option>`;
            }).join('');

        const activityOptions = ['stocking', 'maintenance', 'collection', 'mixed'].map(a =>
            `<option value="${a}">${a.charAt(0).toUpperCase() + a.slice(1)}</option>`
        ).join('');

        const priorityOptions = ['normal', 'high', 'urgent'].map(p =>
            `<option value="${p}">${p.charAt(0).toUpperCase() + p.slice(1)}</option>`
        ).join('');

        const photoHtml = (req.photo_urls || []).length > 0 ? `
            <div class="mb-3">
                <div class="text-muted small mb-1">Photos (${req.photo_urls.length})</div>
                <div class="d-flex flex-wrap gap-2">
                    ${req.photo_urls.map(url =>
                        `<img src="${url}" class="rounded border" style="width:72px;height:72px;object-fit:cover;"
                            onerror="this.style.display='none'">`
                    ).join('')}
                </div>
            </div>
        ` : '';

        const insertFormHtml = isLive ? `
            <hr class="my-3">
            <p class="small text-muted mb-3">
                <i class="bi bi-play-circle-fill text-primary me-1"></i>
                This route is live. You can insert this stop now.
            </p>

            <div class="mb-3">
                <label class="form-label">Label</label>
                <input type="text" class="form-control" name="label"
                    value="${req.label || ''}">
            </div>

            <div class="mb-3">
                <label class="form-label">Activity Type *</label>
                <select class="form-select" name="activity_type">
                    ${activityOptions}
                </select>
            </div>

            <div class="row g-3 mb-3">
                <div class="col-6">
                    <label class="form-label">Dwell Time (min)</label>
                    <input type="number" class="form-control" name="dwell_minutes" min="0" placeholder="e.g. 20">
                </div>
                <div class="col-6">
                    <label class="form-label">Priority</label>
                    <select class="form-select" name="priority">
                        ${priorityOptions}
                    </select>
                </div>
            </div>

            <div class="mb-3">
                <label class="form-label">Insert After Stop</label>
                <select class="form-select" name="after_stop_id">
                    <option value="">Insert at beginning</option>
                    ${afterStopOptions}
                </select>
            </div>

            <div class="mb-4">
                <label class="form-label">Note to Operator</label>
                <textarea class="form-control" name="planner_note" rows="2"
                    placeholder="e.g. Customer on site, priority job">${req.notes || ''}</textarea>
            </div>

            <div class="d-flex justify-content-between gap-2">
                <button type="button" class="btn btn-outline-danger" data-action="reject">
                    Reject
                </button>
                <button type="button" class="btn btn-primary" data-action="approve-insert">
                    <i class="bi bi-plus-circle me-1"></i>Insert Stop &amp; Approve
                </button>
            </div>
        ` : `
            <hr class="my-3">
            <p class="small text-muted mb-3">
                ${this.route
                    ? `<i class="bi bi-clock me-1"></i>Route is <strong>${this.route.status}</strong> — stop can be added once the route is in progress.`
                    : '<i class="bi bi-info-circle me-1"></i>No active route linked to this request.'
                }
            </p>
            <div class="d-flex justify-content-between gap-2">
                <button type="button" class="btn btn-outline-danger" data-action="reject">
                    Reject
                </button>
                <button type="button" class="btn btn-success" data-action="approve-only">
                    <i class="bi bi-check-lg me-1"></i>Approve
                </button>
            </div>
        `;

        return `
            <div class="p-3">
                <div class="mb-3 p-3 bg-light rounded border">
                    <div class="d-flex justify-content-between align-items-start mb-1">
                        <span class="fw-semibold small">${req.label || 'Unlabelled request'}</span>
                        <span class="badge bg-warning text-dark ms-2">Pending</span>
                    </div>
                    <div class="text-muted small mb-1">
                        <i class="bi bi-person-fill me-1"></i>${operatorName}
                    </div>
                    ${req.address ? `
                        <div class="text-muted small mb-1">
                            <i class="bi bi-geo-alt me-1"></i>${req.address}
                        </div>
                    ` : ''}
                    <div class="text-muted small mb-1">
                        <i class="bi bi-diagram-3 me-1"></i>${routeLabel}
                    </div>
                    ${req.notes ? `
                        <div class="mt-2 small fst-italic text-secondary">"${req.notes}"</div>
                    ` : ''}
                    <div class="mt-2 text-muted small">
                        Submitted ${this._formatDatetime(req.created || req.created_at)}
                    </div>
                </div>

                ${photoHtml}

                ${insertFormHtml}
            </div>
        `;
    }
}
